import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../services/errorMessages';
import { FiPlus, FiTrash2, FiEdit2, FiX, FiEye, FiClock, FiFileText, FiUsers, FiPrinter, FiCalendar } from 'react-icons/fi';
import Calendar from '../components/Calendar';
import Dropdown from '../components/Dropdown';
import ConfirmModal from '../components/ConfirmModal';
import './Pages.css';
import './Attachments.css';

const Clients = () => {
  const { isAdmin, isManager, user: currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [cities, setCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [filterCity, setFilterCity] = useState('');

  const openDetail = async (client) => {
    setDetailLoading(true);
    setShowDetail({ client });
    try {
      const { data } = await api.get(`/clients/${client.id}/detail`);
      setShowDetail(data);
    } catch { /* ignored */ }
    finally { setDetailLoading(false); }
  };

  const openView = async (fa) => {
    try {
      const { data } = await api.get(`/filled-attachments/${fa.id}`);
      setViewData(data);
      setShowView(true);
    } catch { /* ignored */ }
  };

  const deleteAttachment = (fa) => {
    setConfirmAction({
      title: 'დანართის სამუდამო წაშლა',
      message: `ნამდვილად გსურთ "${fa.template_title || 'დანართი'}" სამუდამოდ წაშლა? ეს მოქმედება შეუქცევადია!`,
      type: 'delete',
      confirmText: 'სამუდამოდ წაშლა',
      onConfirm: async () => {
        try {
          await api.delete(`/filled-attachments/${fa.id}`);
          // Refresh detail
          if (showDetail?.client) openDetail(showDetail.client);
        } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const formatTimeSec = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const fmtDuration = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}სთ ${m}წთ`;
  };

  const emptyForm = { first_name: '', last_name: '', address: '', birth_date: '', gender: '', personal_number: '', mobile: '', city_id: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchClients = async () => {
    try {
      const { data } = await api.get('/clients');
      setClients(data);
    } catch {
      // fetch failed silently
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const { data } = await api.get('/cities');
      setCities(data);
    } catch { /* ignored */ }
  };

  useEffect(() => { fetchClients(); fetchCities(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
      } else {
        await api.post('/clients', form);
      }
      setShowModal(false);
      setEditing(null);
      setForm(emptyForm);
      fetchClients();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEdit = (client) => {
    setForm({
      ...client,
      birth_date: client.birth_date ? client.birth_date.split('T')[0] : '',
      city_id: client.city_id || '',
    });
    setEditing(client.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirmAction({
      title: 'ბენეფიციარის წაშლა',
      message: 'ნამდვილად გსურთ წაშლა? ეს მოქმედება შეუქცევადია.',
      type: 'delete',
      onConfirm: async () => {
        try { await api.delete(`/clients/${id}`); fetchClients(); } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const filtered = clients.filter((c) => {
    const matchesSearch = `${c.first_name} ${c.last_name} ${c.personal_number}`.toLowerCase().includes(search.toLowerCase());
    const matchesCity = !filterCity || String(c.city_id) === String(filterCity);
    return matchesSearch && matchesCity;
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>ბენეფიციარები</h1>
          <p className="page-subtitle">ბენეფიციარების მართვა</p>
        </div>
        <div className="header-actions">
          <input
            className="search-input"
            placeholder="ძიება..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {!isManager && cities.length > 0 && (
            <Dropdown
              value={filterCity}
              onChange={(val) => setFilterCity(val)}
              options={[{ value: '', label: 'ყველა ქალაქი' }, ...cities.map((c) => ({ value: c.id, label: c.name }))]}
              placeholder="ქალაქი"
            />
          )}
          {isAdmin && (
            <button className="btn-primary" onClick={() => { setForm({ ...emptyForm, city_id: isManager ? (currentUser.city_id || '') : '' }); setEditing(null); setShowModal(true); }}>
              <FiPlus /> დამატება
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">იტვირთება...</div>
      ) : (
        <>
          {/* Card view for mobile */}
          <div className="cards-grid">
            {filtered.map((c) => (
              <div key={c.id} className="data-card">
                <div className="card-header-row">
                  <div className="card-avatar">{c.first_name[0]}{c.last_name[0]}</div>
                  <div>
                    <h3>{c.first_name} {c.last_name}</h3>
                    <span className="card-pn">{c.personal_number}</span>
                  </div>
                </div>
                <div className="card-details">
                  <div className="card-detail"><span>მისამართი:</span> {c.address || '-'}</div>
                  <div className="card-detail"><span>დაბადების თარიღი:</span> {c.birth_date ? new Date(c.birth_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}</div>
                  <div className="card-detail"><span>სქესი:</span> {c.gender === 'male' ? 'მამრობითი' : c.gender === 'female' ? 'მდედრობითი' : '-'}</div>
                  <div className="card-detail"><span>მობილური:</span> {c.mobile || '-'}</div>
                </div>
                {isAdmin && (
                  <div className="card-actions">
                    <button className="btn-icon btn-edit" onClick={() => openDetail(c)} title="დეტალები"><FiEye /></button>
                    <button className="btn-icon btn-edit" onClick={() => handleEdit(c)}><FiEdit2 /></button>
                    <button className="btn-icon btn-danger" onClick={() => handleDelete(c.id)}><FiTrash2 /></button>
                  </div>
                )}
              </div>
            ))}
            {filtered.length === 0 && <div className="empty-state-card">ბენეფიციარები არ არის</div>}
          </div>

          {/* Table view for desktop */}
          <div className="table-container desktop-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th>სახელი</th>
                  <th>გვარი</th>
                  <th>პ/ნ</th>
                  <th>მისამართი</th>
                  <th>დაბ. თარიღი</th>
                  <th>სქესი</th>
                  <th>მობილური</th>
                  {isAdmin && <th>მოქმედება</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td>{c.first_name}</td>
                    <td>{c.last_name}</td>
                    <td>{c.personal_number}</td>
                    <td>{c.address || '-'}</td>
                    <td>{c.birth_date ? new Date(c.birth_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}</td>
                    <td>{c.gender === 'male' ? 'მამრობითი' : c.gender === 'female' ? 'მდედრობითი' : '-'}</td>
                    <td>{c.mobile || '-'}</td>
                    {isAdmin && (
                      <td>
                        <div className="action-btns">
                          <button className="btn-icon btn-edit" onClick={() => openDetail(c)} title="დეტალები"><FiEye /></button>
                          <button className="btn-icon btn-edit" onClick={() => handleEdit(c)} title="რედაქტირება"><FiEdit2 /></button>
                          <button className="btn-icon btn-danger" onClick={() => handleDelete(c.id)} title="წაშლა"><FiTrash2 /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={isAdmin ? "8" : "7"} className="empty-state">ბენეფიციარები არ არის</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditing(null); }}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'რედაქტირება' : 'ახალი ბენეფიციარი'}</h3>
              <button className="btn-icon" onClick={() => { setShowModal(false); setEditing(null); }}><FiX /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form" style={{paddingBottom: '32px'}}>
              {error && <div className="error-message">{error}</div>}
              <div className="form-row">
                <div className="form-group">
                  <label>სახელი</label>
                  <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>გვარი</label>
                  <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>პირადი ნომერი</label>
                <input inputMode="numeric" pattern="[0-9]*" value={form.personal_number} onChange={(e) => setForm({ ...form, personal_number: e.target.value.replace(/\D/g, '') })} required />
              </div>
              <div className="form-group">
                <label>მისამართი</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="form-row">
                <Calendar
                  label="დაბადების თარიღი"
                  value={form.birth_date}
                  onChange={(val) => setForm({ ...form, birth_date: val })}
                  placeholder="აირჩიეთ თარიღი"
                />
                <Dropdown
                  label="სქესი"
                  value={form.gender}
                  onChange={(val) => setForm({ ...form, gender: val })}
                  options={[
                    { value: 'male', label: 'მამრობითი' },
                    { value: 'female', label: 'მდედრობითი' },
                  ]}
                  placeholder="აირჩიეთ სქესი"
                />
              </div>
              <div className="form-group">
                <label>მობილური</label>
                <input inputMode="numeric" pattern="[0-9]*" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '') })} />
              </div>
              <div className="form-group">
                {isManager ? (
                  <>
                    <label>ქალაქი</label>
                    <input value={cities.find(c => c.id === currentUser.city_id)?.name || ''} disabled style={{ opacity: 0.7 }} />
                  </>
                ) : (
                  <Dropdown
                    label="ქალაქი"
                    value={form.city_id}
                    onChange={(val) => setForm({ ...form, city_id: val })}
                    options={cities.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="აირჩიეთ ქალაქი"
                  />
                )}
              </div>
              <button type="submit" className="btn-primary btn-full" disabled={!form.first_name.trim() || !form.last_name.trim() || !form.personal_number}>{editing ? 'შენახვა' : 'დამატება'}</button>
            </form>
          </div>
        </div>
      )}

      {/* Client detail modal */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showDetail.client?.first_name} {showDetail.client?.last_name}</h3>
              <button className="btn-icon" onClick={() => setShowDetail(null)}><FiX /></button>
            </div>
            {detailLoading ? <div className="loading" style={{padding:40}}>იტვირთება...</div> : showDetail.stats && (
              <div className="client-detail-view">
                {/* Client info */}
                <div className="cdv-info">
                  <div className="cdv-avatar">{showDetail.client.first_name[0]}{showDetail.client.last_name[0]}</div>
                  <div className="cdv-main">
                    <h2>{showDetail.client.first_name} {showDetail.client.last_name}</h2>
                    <span className="cdv-pn">{showDetail.client.personal_number}</span>
                  </div>
                </div>
                <div className="cdv-fields">
                  {showDetail.client.mobile && <div className="cdv-field"><span>მობილური</span><strong>{showDetail.client.mobile}</strong></div>}
                  {showDetail.client.address && <div className="cdv-field"><span>მისამართი</span><strong>{showDetail.client.address}</strong></div>}
                  {showDetail.client.birth_date && <div className="cdv-field"><span>დაბადების თარიღი</span><strong>{new Date(showDetail.client.birth_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong></div>}
                  {showDetail.client.gender && <div className="cdv-field"><span>სქესი</span><strong>{showDetail.client.gender === 'male' ? 'მამრობითი' : 'მდედრობითი'}</strong></div>}
                </div>

                {/* Stats */}
                <div className="cdv-stats">
                  <div className="cdv-stat"><FiFileText /><div><strong>{showDetail.stats.total_attachments}</strong><span>სულ დანართი</span></div></div>
                  <div className="cdv-stat"><FiClock /><div><strong>{fmtDuration(showDetail.stats.total_duration)}</strong><span>ჯამური დრო</span></div></div>
                  <div className="cdv-stat"><FiUsers /><div><strong>{(showDetail.users || []).length}</strong><span>თანამშრომელი</span></div></div>
                </div>

                {/* Assigned users */}
                {showDetail.users && showDetail.users.length > 0 && (
                  <div className="cdv-section">
                    <h4>პერსონალური კონსულტანტის სახელი და გვარი</h4>
                    <div className="cdv-users">
                      {showDetail.users.map((u) => (
                        <div key={u.id} className="cdv-user-chip">
                          <span className="cdv-chip-av">{u.first_name[0]}{u.last_name[0]}</span>
                          {u.first_name} {u.last_name}
                          <span className="cdv-chip-pn">{u.personal_number}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachment history */}
                <div className="cdv-section">
                  <h4>შევსებული დანართები ({showDetail.attachments?.length || 0})</h4>
                  <div className="cdv-period-stats">
                    <span>დღეს: <strong>{showDetail.stats.today}</strong></span>
                    <span>კვირაში: <strong>{showDetail.stats.week}</strong></span>
                    <span>თვეში: <strong>{showDetail.stats.month}</strong></span>
                  </div>
                  <div className="cdv-att-list">
                    {(showDetail.attachments || []).map((fa) => (
                      <div key={fa.id} className={`cdv-att-row ${fa.hidden_by_user ? 'cdv-att-hidden' : ''}`} onClick={() => openView(fa)} style={{ cursor: 'pointer' }}>
                        <FiEye style={{ color: '#14b8a6', flexShrink: 0 }} />
                        <div className="cdv-att-info">
                          <span className="cdv-att-title">
                            {fa.template_title}
                            {fa.hidden_by_user && <span className="att-hidden-badge">წაშლილი</span>}
                          </span>
                          <span className="cdv-att-meta">
                            {fa.user_first_name} {fa.user_last_name} | {(() => { const d = new Date(fa.completed_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}, ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}
                          </span>
                        </div>
                        {fa.duration_seconds > 0 && <span className="cdv-att-dur"><FiClock /> {fmtDuration(fa.duration_seconds)}</span>}
                        <button className="btn-icon btn-danger" onClick={(e) => { e.stopPropagation(); deleteAttachment(fa); }} title="სამუდამო წაშლა"><FiTrash2 /></button>
                      </div>
                    ))}
                    {(!showDetail.attachments || showDetail.attachments.length === 0) && <div className="empty-hint" style={{padding:16, textAlign:'center'}}>დანართები არ არის</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* View filled attachment modal */}
      {showView && viewData && (
        <div className="modal-overlay" onClick={() => setShowView(false)}>
          <div className="modal attachment-modal print-area" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header no-print">
              <h3>{viewData.template_title}</h3>
              <span className="fill-date-badge">
                <FiCalendar />
                {(() => { const d = new Date(viewData.completed_at); const days = ['კვირა','ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი']; return `${days[d.getDay()]}, ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-icon" onClick={() => window.print()} title="ბეჭდვა"><FiPrinter /></button>
                <button className="btn-icon btn-danger" onClick={() => { setShowView(false); deleteAttachment(viewData); }} title="წაშლა"><FiTrash2 /></button>
                <button className="btn-icon" onClick={() => setShowView(false)}><FiX /></button>
              </div>
            </div>
            <div className="paper">
              <div className="paper-title-right">{viewData.template_title}</div>
              {viewData.template_main_title && <div className="paper-center"><div className="paper-main-title">{viewData.template_main_title}</div></div>}
              {viewData.template_subtitle && <div className="paper-center"><div className="paper-subtitle">{viewData.template_subtitle}</div></div>}
              <div className="paper-fields">
                {(viewData.template_header_fields || []).map((f, i) => (
                  <div key={i} className="paper-field">
                    <span className="paper-field-label">{f.label}</span>
                    <span className="paper-field-value">
                      {f.type === 'client_dropdown' ? `${viewData.client_first_name || ''} ${viewData.client_last_name || ''} - ${viewData.client_pn || ''}` :
                       f.type === 'auto_user' ? `${viewData.user_first_name || ''} ${viewData.user_last_name || ''} - ${viewData.user_pn || ''}` :
                       (viewData.header_values || {})[i] || ''}
                    </span>
                  </div>
                ))}
              </div>
              {(() => {
                const cols = (viewData.template_columns || []).map((c, i) => ({ ...c, idx: i }));
                const rd = viewData.row_data || {};
                // Detect weekly: days contain plain objects (not arrays) for numeric keys
                const isWeekly = rd.type === 'weekly' || (rd.days && rd.weekStart && (() => {
                  const numKeys = Object.keys(rd.days).filter(k => !isNaN(parseInt(k)));
                  return numKeys.length > 0 && numKeys.some(k => rd.days[k] && !Array.isArray(rd.days[k]) && typeof rd.days[k] === 'object');
                })());

                // Calendar view
                if (!isWeekly && (rd.type === 'calendar' || rd.days)) {
                  const days = rd.days || {};
                  const weekStart = rd.weekStart;
                  const dayNames = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];
                  return (
                    <div className="paper-calendar-view">
                      {weekStart && <div className="paper-week-label">კვირა: {weekStart}</div>}
                      {Object.keys(days).sort((a, b) => a - b).map((dayIdx) => {
                        const entries = days[dayIdx] || [];
                        const monday = weekStart ? new Date(weekStart) : null;
                        let dateStr = '';
                        if (monday) { const d = new Date(monday); d.setDate(d.getDate() + parseInt(dayIdx)); dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
                        return (
                          <div key={dayIdx} className="paper-cal-day">
                            <div className="paper-cal-day-header"><strong>{dayNames[dayIdx]}</strong>{dateStr && <span>{dateStr}</span>}</div>
                            {entries.length > 0 && entries.some(e => Object.values(e).some(v => v)) ? (
                              <table className="paper-table" style={{marginBottom: 0}}>
                                <thead><tr><th className="paper-col-num">№</th>{cols.map((c) => <th key={c.idx} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>)}</tr></thead>
                                <tbody>{entries.filter(e => Object.values(e).some(v => v)).map((entry, ei) => (
                                  <tr key={ei}><td className="paper-col-num">{ei + 1}</td>{cols.map((c) => <td key={c.idx}>{entry[c.idx] || ''}</td>)}</tr>
                                ))}</tbody>
                              </table>
                            ) : (<div style={{padding: '8px 12px', color: '#94a3b8', fontSize: 13}}>ჩანაწერები არ არის</div>)}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Weekly plan view
                if (isWeekly) {
                  const days = rd.days || {};
                  const weekStart = rd.weekStart;
                  const visibleCols = cols.filter(c => c.type !== 'datetime' && c.type !== 'duration');
                  const dayKeys = Object.keys(days).filter(k => k !== '_fullRow' && k !== '_skipDays' && !isNaN(parseInt(k))).sort((a, b) => a - b);
                  const dayNames = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];
                  let weekLabel = weekStart || '';
                  if (weekStart) { const ws = new Date(weekStart); const we = new Date(ws); we.setDate(we.getDate() + 6); weekLabel = `${ws.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} — ${we.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`; }
                  return (
                    <div className="paper-table-wrap">
                      {weekStart && <div className="paper-week-label" style={{marginBottom: 12}}>კვირა: {weekLabel}</div>}
                      <table className="paper-table">
                        <thead><tr><th>დღე</th><th>თარიღი</th>{visibleCols.map((c) => <th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>)}</tr></thead>
                        <tbody>
                          {dayKeys.map((dayIdx, di) => {
                            const raw = days[dayIdx] || {};
                            const entries = Array.isArray(raw) ? raw : [raw];
                            const isSkipped = entries[0]?._skipped || raw._skipped;
                            const monday = weekStart ? new Date(weekStart) : null;
                            let dateStr = '';
                            if (monday) { const d = new Date(monday); d.setDate(d.getDate() + parseInt(dayIdx)); dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
                            const totalRows = dayKeys.reduce((sum, k) => { const r = days[k]; return sum + (Array.isArray(r) ? r.length : 1); }, 0);
                            return entries.map((entryData, ei) => (
                              <tr key={`${dayIdx}-${ei}`} style={isSkipped ? {opacity: 0.4} : {}}>
                                {ei === 0 && <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }} rowSpan={entries.length}>{dayNames[dayIdx] || `დღე ${parseInt(dayIdx) + 1}`}</td>}
                                {ei === 0 && <td style={{ whiteSpace: 'nowrap' }} rowSpan={entries.length}>{dateStr}</td>}
                                {visibleCols.map((c) => {
                                  if (c.fullRow && di === 0 && ei === 0) {
                                    const fullRowObj = days._fullRow || {};
                                    const val = fullRowObj[c.idx] || fullRowObj[String(c.idx)] || '';
                                    return (<td key={c.idx} rowSpan={totalRows} className="paper-cell-fullrow" style={{ verticalAlign: 'top', fontSize: 13, whiteSpace: 'pre-wrap' }}>{c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 25, objectFit: 'contain' }} /> : val}</td>);
                                  }
                                  if (c.fullRow) return null;
                                  const cellVal = entryData[c.idx] || '';
                                  const strVal = typeof cellVal === 'string' ? cellVal : String(cellVal || '');
                                  return (<td key={c.idx} style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>{isSkipped ? '—' : c.type === 'signature' && cellVal ? <img src={cellVal} alt="" style={{ height: 25, objectFit: 'contain' }} /> : c.type === 'worktime' && strVal ? (
                                    <span className="tp-view">{strVal.split(' - ').map((t, ti) => <span key={ti} className="tp-view-time">{t}</span>).reduce((a, b) => [a, <span key="arrow" className="tp-view-arrow">→</span>, b])}</span>
                                  ) : c.type === 'activity' && strVal && strVal.includes(' || ') ? (
                                    strVal.split(' || ').filter(Boolean).map((act, ai) => <div key={ai} style={{ padding: '1px 0' }}>• {act}</div>)
                                  ) : strVal}</td>);
                                })}
                              </tr>
                            ));
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                // Standard view
                const rows = rd.rows || (Array.isArray(rd) ? rd : []);
                const rawFullRows = rd.fullRows || {};
                const fullRows = {};
                Object.keys(rawFullRows).forEach(k => { fullRows[Number(k)] = rawFullRows[k]; });
                const rowCount = Math.max(rows.length, 1);
                return (
                  <div className="paper-table-wrap">
                    <table className="paper-table">
                      <thead><tr><th className="paper-col-num">№</th>
                        {cols.map((c) => (<th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>))}
                      </tr></thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri}>
                            <td className="paper-col-num">{ri + 1}</td>
                            {cols.map((c) => {
                              if (c.fullRow && ri === 0) {
                                const items = fullRows[c.idx] || [];
                                return (
                                  <td key={c.idx} rowSpan={rowCount} className="paper-cell-fullrow" style={{ verticalAlign: 'middle', fontSize: 13 }}>
                                    {items.map((val, vi) => (<div key={vi} style={{ padding: '2px 0' }}>{c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 25, objectFit: 'contain' }} /> : c.type === 'activity' && val && val.includes(' || ') ? val.split(' || ').filter(Boolean).map((act, ai) => <div key={ai} style={{ padding: '1px 0' }}>• {act}</div>) : (val || '')}</div>))}
                                  </td>
                                );
                              }
                              if (c.fullRow) return null;
                              return (<td key={c.idx}>{c.type === 'signature' && row[c.idx] ? <img src={row[c.idx]} alt="" style={{ height: 25, objectFit: 'contain' }} /> : (row[c.idx] || '')}</td>);
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {viewData.note && <div className="paper-note"><strong>შენიშვნა:</strong> {viewData.note}</div>}
              {false && viewData.duration_seconds && (
                <div className="paper-duration">ხანგრძლივობა: {formatTimeSec(viewData.duration_seconds)}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}
    </div>
  );
};

export default Clients;
